// Console D's small instruments (the mock's core.js): a share bar, a sparkline, bars, two
// stacked steps and a sankey. Ink on hairlines, one accent at most, every chart named for
// screen readers. LineChart (../LineChart.tsx) is the line instrument. Charts with axes draw at
// their container's real width (viewBox = pixels), so their text keeps its size on a phone.
import type { ReactNode } from 'react'
import { ChartLegend, useWidth } from '../LineChart'

const fmt2 = (v: number) =>
  Math.abs(v) >= 100 ? v.toFixed(0) : Math.abs(v) >= 10 ? v.toFixed(1) : v.toFixed(2)

/** A 64 px share bar beside a number; `bad` and `warn` tint the fill. */
export function Hbar({ share, tone }: { share: number; tone?: 'bad' | 'warn' }) {
  const pct = Math.max(0, Math.min(100, share * 100))
  return (
    <span className={tone ? `hbar ${tone}` : 'hbar'} aria-hidden="true">
      <i style={{ width: `${pct}%` }} />
    </span>
  )
}

/**
 * A 64×18 sparkline. Decoration: the numbers beside it carry the meaning. A null is a gap (a day
 * with nothing to measure), never a zero: the line breaks there.
 */
export function Spark({ values }: { values: readonly (number | null)[] }) {
  // The tallest value fills the height, whatever its scale (a count, a rate).
  const max = Math.max(...values.map((v) => v ?? 0)) || 1
  const n = values.length
  const x = (i: number) => (n > 1 ? (i / (n - 1)) * 62 + 1 : 32)
  const y = (v: number) => 16 - (v / max) * 14
  // Runs of measured points between the gaps.
  const runs: [number, number][][] = [[]]
  values.forEach((v, i) => {
    if (v === null) runs.push([])
    else runs.at(-1)?.push([x(i), y(v)])
  })
  return (
    <svg className="spark" viewBox="0 0 64 18" aria-hidden="true" focusable="false">
      {runs
        .filter((r) => r.length > 0)
        .map((r) => {
          const [first] = r as [[number, number]]
          // A lone point between gaps still shows, as a one-unit tick.
          const pts = r.length > 1 ? r : [first, [first[0] + 1, first[1]]]
          return <polyline key={first[0]} points={pts.map((p) => p.join(',')).join(' ')} />
        })}
    </svg>
  )
}

/** One series of bars: its name (for the legend) and a value per category; null draws nothing. */
/** A percentile line's label, placed: beside its line, `end` when it reads to the left. */
export interface MarkLabel {
  x: number
  y: number
  end: boolean
}

/**
 * Labels for vertical lines that never overprint (APS-382): each sits right of its line, or left
 * of it when the right side runs past the plot or into the next line; one that fits neither way
 * drops a row. A label crosses another line only when no row keeps it clear.
 */
export function placeMarkLabels(
  marks: readonly { x: number; text: string }[],
  left: number,
  right: number,
  top: number,
  row = 12,
): MarkLabel[] {
  const CHAR = 6.6
  const order = marks.map((_, i) => i).sort((a, b) => (marks[a]?.x ?? 0) - (marks[b]?.x ?? 0))
  const placed: { from: number; to: number; y: number }[] = []
  const out: MarkLabel[] = marks.map(() => ({ x: 0, y: top, end: false }))
  for (const i of order) {
    const m = marks[i] as { x: number; text: string }
    const w = m.text.length * CHAR
    const sides = [
      { from: m.x + 5, to: m.x + 5 + w, end: false },
      { from: m.x - 5 - w, to: m.x - 5, end: true },
    ].filter((s) => s.from >= left && s.to <= right)
    const options = sides.length > 0 ? sides : [{ from: m.x - 5 - w, to: m.x - 5, end: true }]
    const crosses = (s: { from: number; to: number }) =>
      marks.some((o, j) => j !== i && o.x >= s.from - 2 && o.x <= s.to + 2)
    const free = (s: { from: number; to: number }, y: number) =>
      !placed.some((p) => p.y === y && s.from < p.to + 4 && p.from < s.to + 4)
    let pick: { from: number; to: number; end: boolean; y: number } | undefined
    for (const clear of [true, false])
      for (let r = 0; r < marks.length && !pick; r++)
        for (const s of options)
          if (!pick && free(s, top + r * row) && (!clear || !crosses(s)))
            pick = { ...s, y: top + r * row }
    const at = pick ?? { ...(options[0] as (typeof options)[number]), y: top }
    placed.push(at)
    out[i] = { x: at.end ? m.x - 5 : m.x + 5, y: at.y, end: at.end }
  }
  return out
}

export interface BarSeries {
  name: string
  values: readonly (number | null)[]
}

export interface BarsProps {
  /** One series, as the mock draws bars. Or `series`. */
  values?: readonly number[]
  /** Several series side by side in each category, in steps of ink, with a legend. */
  series?: readonly BarSeries[]
  label: string
  width?: number
  height?: number
  max?: number
  format?: (v: number) => string
  /** x labels: [index, text]. */
  xLabels?: readonly (readonly [number, string])[]
  /**
   * A label per category, centred under its bar (instead of `xLabels`): every one while the slots
   * are wide enough, shortened with an ellipsis (the full text in a title); thinned when not.
   */
  categories?: readonly string[]
  /** The share of each slot left empty between bars (0 to 0.6); 2 px when unset. */
  gapShare?: number
  /** Bars from this index on are full ink (the rest are a step lighter). */
  hiFrom?: number
  /** A class per bar (b1, b1 hi, b2, b3, bw, bc). */
  classes?: readonly (string | undefined)[]
  /** A dashed release marker before a bar. */
  marker?: { index: number; label: string }
  /** Percentile lines before a bar (p50, p90), dashed in the degraded colour. */
  percentiles?: readonly { index: number; label: string }[]
  /** A budget or an alert's limit: a dashed horizontal line with its label. */
  threshold?: { value: number; label: string } | undefined
}

/**
 * Bars on an axis from zero (below it for negative values) with three gridlines. One series is
 * the mock's bars; several sit side by side in each category, as the line chart's steps of ink.
 */
export function Bars({
  values,
  series,
  label,
  width: fixedWidth,
  height = 160,
  max: maxIn,
  format = fmt2,
  xLabels = [],
  categories,
  gapShare,
  hiFrom,
  classes,
  marker,
  percentiles = [],
  threshold,
}: BarsProps) {
  const [ref, measured] = useWidth(640)
  const width = fixedWidth ?? measured
  const L = 40
  const R = 12
  const T = 12
  const B = 22
  const sets: readonly (readonly (number | null)[])[] = series
    ? series.map((x) => x.values)
    : [values ?? []]
  const several = sets.length > 1
  const n = Math.max(0, ...sets.map((x) => x.length))
  const known = [
    ...sets.flatMap((x) => x.filter((v): v is number => v !== null)),
    ...(threshold ? [threshold.value] : []),
  ]
  const min = Math.min(0, ...known)
  const max = maxIn ?? Math.max(0, ...known)
  const top = max > min ? max : min + 1
  const bw = (width - L - R) / Math.max(1, n)
  const y = (v: number) => T + (1 - (v - min) / (top - min)) * (height - T - B)
  const out: ReactNode[] = []
  for (let k = 0; k <= 2; k++) {
    const v = min + (k / 2) * (top - min)
    out.push(
      <g key={`g${k}`}>
        <line className="grid" x1={L} x2={width - R} y1={y(v)} y2={y(v)} />
        <text className="ax" x={L - 6} y={y(v) + 3.5} textAnchor="end">
          {format(v)}
        </text>
      </g>,
    )
  }
  const gap = gapShare === undefined ? 2 : bw * Math.min(0.6, Math.max(0, gapShare))
  const inner = Math.max(1, (bw - gap) / sets.length)
  for (const [s, set] of sets.entries())
    for (const [i, v] of set.entries()) {
      if (v === null) continue
      const cls = several
        ? `vbar s${s}`
        : (classes?.[i] ?? (hiFrom !== undefined && i >= hiFrom ? 'b1 hi' : 'b1'))
      out.push(
        <rect
          key={`b${s}-${i}`}
          className={cls}
          x={L + i * bw + gap / 2 + s * inner}
          y={Math.min(y(v), y(0))}
          width={Math.max(1, inner - (several ? 1 : 0))}
          height={Math.abs(y(0) - y(v))}
        />,
      )
    }
  out.push(<line key="base" className="base" x1={L} x2={width - R} y1={y(0)} y2={y(0)} />)
  // Every category while its slot holds at least a few characters, shortened to the slot;
  // otherwise thinned to what fits (at most 3 on a phone and 6 wider, fewer when they are long).
  const CHAR = 6.6
  const slotChars = Math.floor((bw - 6) / CHAR)
  const longest = Math.max(0, ...(categories ?? []).map((c) => c.length))
  const fit = Math.max(1, Math.floor((width - L - R) / (longest * CHAR + 12)))
  const every =
    categories && slotChars >= 4
      ? 1
      : Math.max(1, Math.ceil(n / Math.min(width < 480 ? 3 : 6, fit)))
  const short = (text: string) =>
    every === 1 && text.length > slotChars ? `${text.slice(0, Math.max(1, slotChars - 1))}…` : text
  const xs: readonly (readonly [number, string])[] = categories
    ? categories.flatMap((text, i) => (i % every === 0 ? [[i, text] as const] : []))
    : xLabels
  for (const [i, text] of xs) {
    // Category labels sit centred under their bar; the mock's x labels keep their edge anchors.
    const centred = !!categories
    const shown = centred ? short(text) : text
    out.push(
      <text
        key={`x${i}`}
        className="ax"
        x={L + i * bw + bw / 2}
        y={height - 6}
        textAnchor={centred ? 'middle' : i === 0 ? 'start' : i >= n - 1 ? 'end' : 'middle'}
      >
        {shown !== text ? <title>{text}</title> : null}
        {shown}
      </text>,
    )
  }
  const marks = percentiles.map((p) => ({ x: L + p.index * bw, text: p.label }))
  const at = placeMarkLabels(marks, L, width - R, T + 9)
  for (const [i, p] of percentiles.entries()) {
    const px = marks[i]?.x ?? 0
    const l = at[i]
    out.push(
      <g key={`p${p.index}${p.label}`}>
        <line className="thr" x1={px} x2={px} y1={T} y2={height - B} />
        <text
          className="thr-t"
          x={l?.x ?? px + 5}
          y={l?.y ?? T + 9}
          {...(l?.end ? { textAnchor: 'end' } : {})}
        >
          {p.label}
        </text>
      </g>,
    )
  }
  if (threshold)
    out.push(
      <g key="thr">
        <line
          className="thr"
          x1={L}
          x2={width - R}
          y1={y(threshold.value)}
          y2={y(threshold.value)}
        />
        <text className="thr-t" x={width - R} y={y(threshold.value) - 5} textAnchor="end">
          {threshold.label}
        </text>
      </g>,
    )
  if (marker) {
    const px = L + marker.index * bw
    out.push(
      <g key="mk">
        <line className="mk" x1={px} x2={px} y1={T - 4} y2={height - B} />
        <text className="mk-t" x={px + 5} y={T + 6}>
          {marker.label}
        </text>
      </g>,
    )
  }
  return (
    <div ref={ref}>
      <svg
        className="chart"
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={label}
      >
        {out}
      </svg>
      {several && series ? <ChartLegend names={series.map((x) => x.name)} box /> : null}
    </div>
  )
}

/** Two stacked ink steps a bar: `a` (full ink) over `b` (a step lighter). */
export function StackBars({
  a,
  b,
  label,
  width: fixedWidth,
  height = 120,
  max: maxIn,
  xLabels = [],
  marker,
}: {
  a: readonly number[]
  b: readonly number[]
  label: string
  width?: number
  height?: number
  max?: number
  xLabels?: readonly (readonly [number, string])[]
  marker?: { index: number; label: string }
}) {
  const [ref, measured] = useWidth(640)
  const width = fixedWidth ?? measured
  const L = 32
  const R = 8
  const T = 12
  const B = 22
  const n = a.length
  const bw = (width - L - R) / Math.max(1, n)
  const max = maxIn ?? Math.max(...a.map((v, i) => v + (b[i] ?? 0)), 0)
  // Counts: whole-number ticks only, so the top is even (its middle a whole number) or 1.
  const top = max > 1 ? Math.ceil(max / 2) * 2 : 1
  const y = (v: number) => T + (1 - v / top) * (height - T - B)
  const out: ReactNode[] = []
  for (const v of top > 1 ? [0, top / 2, top] : [0, 1]) {
    out.push(
      <g key={`g${v}`}>
        <line className="grid" x1={L} x2={width - R} y1={y(v)} y2={y(v)} />
        <text className="ax" x={L - 6} y={y(v) + 3.5} textAnchor="end">
          {v}
        </text>
      </g>,
    )
  }
  a.forEach((av, i) => {
    const x0 = L + i * bw + 3
    const w = Math.max(1, bw - 6)
    const hb = height - B - y(b[i] ?? 0)
    const ha = height - B - y(av)
    out.push(
      // biome-ignore lint/suspicious/noArrayIndexKey: bars are positional
      <g key={`s${i}`}>
        <rect className="b2" x={x0} y={height - B - hb} width={w} height={hb} />
        <rect className="b1 hi" x={x0} y={height - B - hb - ha} width={w} height={ha} />
      </g>,
    )
  })
  out.push(
    <line key="base" className="base" x1={L} x2={width - R} y1={height - B} y2={height - B} />,
  )
  for (const [i, text] of xLabels)
    out.push(
      <text
        key={`x${i}`}
        className="ax"
        x={L + i * bw + (i ? bw : 0)}
        y={height - 6}
        textAnchor={i ? 'end' : 'start'}
      >
        {text}
      </text>,
    )
  if (marker) {
    const px = L + marker.index * bw
    const span = marker.label.length * 6.6 + 5
    const tall = (i: number) => y((a[i] ?? 0) + (b[i] ?? 0)) < 20
    const rightBlocked = (at: number) => {
      if (px + span > width - R) return true
      for (let i = at; i < n && L + i * bw < px + span; i++) if (tall(i)) return true
      return false
    }
    out.push(
      <g key="mk">
        <line className="mk" x1={px} x2={px} y1={4} y2={height - B} />
        <text
          className="mk-t"
          // Right of the line, unless the bars there reach the label or the edge cuts it.
          {...(rightBlocked(marker.index) ? { x: px - 5, textAnchor: 'end' } : { x: px + 5 })}
          y={12}
        >
          {marker.label}
        </text>
      </g>,
    )
  }
  return (
    <div ref={ref}>
      <svg
        className="chart"
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={label}
      >
        {out}
      </svg>
    </div>
  )
}

export interface SankeyNode {
  id: string
  label: string
  value: number
  /** bad (crash colour), acc (the view's one accent), dim. */
  tone?: 'bad' | 'acc' | 'dim'
}
export interface SankeyLink {
  source: string
  target: string
  value: number
  tone?: 'bad' | 'acc'
}

/**
 * Paths as bands between columns of nodes (journeys, the paths before an exit). Drawn at its
 * container's width, like the mock's, so labels stay 11 px and the chart 260 px tall.
 */
export function Sankey({
  columns,
  links,
  label,
  width: fixedWidth,
  height = 260,
  right = 150,
  gap = 12,
  format = (v: number) => String(v),
}: {
  columns: readonly (readonly SankeyNode[])[]
  links: readonly SankeyLink[]
  label: string
  width?: number
  height?: number
  /** Room for the last column's labels. */
  right?: number
  gap?: number
  format?: (v: number) => string
}) {
  const [ref, measured] = useWidth(640)
  const width = fixedWidth ?? measured
  const L = 2
  const T = 6
  const B = 6
  const nw = 8
  const nc = columns.length
  const totals = columns.map((c) => c.reduce((a, n) => a + n.value, 0))
  const maxN = Math.max(...columns.map((c) => c.length), 1)
  const k = (height - T - B - (maxN - 1) * gap) / Math.max(...totals, 1)
  const pos = new Map<
    string,
    { x: number; y: number; h: number; n: SankeyNode; out: number; in: number }
  >()
  columns.forEach((c, i) => {
    const x = L + (nc === 1 ? 0 : (i * (width - L - right - nw)) / (nc - 1))
    let y = T
    for (const n of c) {
      const h = Math.max(2, n.value * k)
      pos.set(n.id, { x, y, h, n, out: y, in: y })
      y += h + gap
    }
  })
  const bands: ReactNode[] = []
  links.forEach((l, i) => {
    const a = pos.get(l.source)
    const b = pos.get(l.target)
    if (!a || !b) return
    const h = Math.max(1, l.value * k)
    const x0 = a.x + nw
    const x1 = b.x
    const xm = (x0 + x1) / 2
    const y0 = a.out
    const y1 = b.in
    a.out += h
    b.in += h
    bands.push(
      <path
        // biome-ignore lint/suspicious/noArrayIndexKey: links keep their order
        key={i}
        className={l.tone ? `sk-link ${l.tone}` : 'sk-link'}
        d={`M${x0},${y0} C${xm},${y0} ${xm},${y1} ${x1},${y1} L${x1},${y1 + h} C${xm},${y1 + h} ${xm},${y0 + h} ${x0},${y0 + h} Z`}
      >
        <title>{`${a.n.label} → ${b.n.label}: ${format(l.value)}`}</title>
      </path>,
    )
  })
  return (
    <div ref={ref}>
      <svg
        className="chart sk"
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={label}
      >
        {bands}
        {[...pos.values()].map((p) => (
          <g key={p.n.id}>
            <rect
              className={p.n.tone ? `sk-node ${p.n.tone}` : 'sk-node'}
              x={p.x}
              y={p.y}
              width={nw}
              height={p.h}
              rx={1.5}
            />
            <text className="sk-t" x={p.x + nw + 6} y={p.y + Math.min(p.h / 2, 11) + 4}>
              {p.n.label} <tspan className="sk-v">{format(p.n.value)}</tspan>
            </text>
          </g>
        ))}
      </svg>
    </div>
  )
}
